import baseJSON from './base.json';
import otpJSON from './otp.json';
import passwordMigrationJSON from './passwordMigrate.json';
import passwordResetJSON from './passwordReset.json';
import googleJSON from './google.json';
import accountLinkingJSON from './accountLinking.json';
import mfaJSON from './mfa.json';
import conditionalAccessJSON from './conditionalAccess.json';
import webAuthJSON from './webAuth.json';
import { cloneDeep } from 'lodash';
import jsonXMLsettings from '../../../constants/jsonXMLsettings'
import jsonConstants from '../../../constants/jsonConstants';
import defaultEnvs from '../../../constants/defaultEnvs';
import { getActiveDefaultEnvironments } from '../utils';

interface templateGeneraterOptions {
  passwordMigration: {
    selectedOption: 'resetPassword' | 'migratePasswords';
  };
  social: {
    google: {
      value: boolean;
      linkAccounts: boolean;
    };
  };
  otp: {
    choiceBetweenEmailPhone: {
      value: boolean;
    };
  };
  conditionalAccess: {
    //used to be signIn_enableMFA
    value: boolean;
  };
  biometric: {
    value: boolean;
  };
  mfa: {
    // for requiring all sign ins to go through MFA
    value: boolean;
  };
}

export const generateTemplate = (options: templateGeneraterOptions) => {
  const _baseFile: any = cloneDeep(baseJSON);
  const addLogRocketHTML = jsonXMLsettings.instrumentPolicyHTMLWithLR;
  console.log('initial _baseFile :', JSON.parse(JSON.stringify(_baseFile)));
  if (options['passwordMigration']['selectedOption'] === 'resetPassword') {
    const _passwordResetJSON: any = passwordResetJSON;
    // add nodeData information
    addNodeData(_baseFile, _passwordResetJSON);
    // add component, nodes and edges
    updateFC(_baseFile, _passwordResetJSON);
    addTemplatizations(_baseFile, _passwordResetJSON);
  }
  if (options['passwordMigration']['selectedOption'] === 'migratePasswords') {
    const _passwordMigrationJSON: any = passwordMigrationJSON;
    // add nodeData information
    addNodeData(_baseFile, _passwordMigrationJSON);
    // add component, nodes and edges
    updateFC(_baseFile, _passwordMigrationJSON);
    // add claims
    addClaims(_baseFile, _passwordMigrationJSON);
    // add AADAttributes
    addAADAttributes(_baseFile, _passwordMigrationJSON);
    addTemplatizations(_baseFile, _passwordMigrationJSON);
  }
  if (options['social']['google']['value'] && !options['social']['google']['linkAccounts']) {
    const _googleJSON: any = googleJSON;
    // add nodeData information
    addNodeData(_baseFile, _googleJSON);
    // add component, nodes and edges
    updateFC(_baseFile, _googleJSON);
    if (_googleJSON.hasOwnProperty('updateNode') && _googleJSON['updateNode']['id']) {
      _baseFile['nodeData'][_googleJSON['updateNode']['id']]['links']['socialProviders']['google'] = true;
    }
    addTemplatizations(_baseFile, _googleJSON);
  }
  if (options['social']['google']['value'] && options['social']['google']['linkAccounts']) {
    const _accountLinkingJSON: any = accountLinkingJSON;
    // add nodeData information
    addNodeData(_baseFile, _accountLinkingJSON);
    // add component, nodes and edges
    updateFC(_baseFile, _accountLinkingJSON);
    // add claims
    addClaims(_baseFile, _accountLinkingJSON);
    if (_accountLinkingJSON.hasOwnProperty('updateNode') && _accountLinkingJSON['updateNode']['id']) {
      _baseFile['nodeData'][_accountLinkingJSON['updateNode']['id']]['links']['socialProviders']['google'] = true;
    }
    addTemplatizations(_baseFile, _accountLinkingJSON);
  }
  if (options['otp']['choiceBetweenEmailPhone']['value']) {
    const _otpJSON: any = otpJSON;
    // add nodeData information
    addNodeData(_baseFile, _otpJSON);
    // add component, nodes and edges
    updateFC(_baseFile, _otpJSON);
    // add claims
    addClaims(_baseFile, _otpJSON);
    if (_otpJSON.hasOwnProperty('updateNode') && _otpJSON['updateNode']['id']) {
      _baseFile['nodeData'][_otpJSON['updateNode']['id']]['links']['otp'] = true;
    }
    addTemplatizations(_baseFile, _otpJSON);
  }

  if (options['conditionalAccess']['value']) {
    const _mfaJSON: any = mfaJSON;
    // add nodeData information
    addNodeData(_baseFile, _mfaJSON);
    // add component, nodes and edges
    updateFC(_baseFile, _mfaJSON);
    // add claims
    addClaims(_baseFile, _mfaJSON);
    const _conditionalAccessJSON: any = conditionalAccessJSON;
    addNodeData(_baseFile, _conditionalAccessJSON);
    updateFC(_baseFile, _conditionalAccessJSON);
    addClaims(_baseFile, _conditionalAccessJSON);
    addTemplatizations(_baseFile, _conditionalAccessJSON);
  } else if (options['mfa']['value']) {
    const _mfaJSON: any = mfaJSON;
    // add nodeData information
    addNodeData(_baseFile, _mfaJSON);
    // add component, nodes and edges
    updateFC(_baseFile, _mfaJSON);
    // add claims
    addClaims(_baseFile, _mfaJSON);
    addTemplatizations(_baseFile, _mfaJSON);
  }
  if (options['biometric']['value']) {
    const _webAuthJSON: any = webAuthJSON;
    // add nodeData information
    addNodeData(_baseFile, _webAuthJSON);
    // add component, nodes and edges
    updateFC(_baseFile, _webAuthJSON);
    // add claims
    addClaims(_baseFile, _webAuthJSON);
    addTemplatizations(_baseFile, _webAuthJSON);
    if (options['conditionalAccess']['value']) {
      const fcId: string = 'userjourney-bb6df4e5-9def-7f5e-7a33-6ac253de56d8';
      [
        {
          source: 'aadUpdate-35d506eb-4d1b-3a55-9ed0-a60f0344187b',
          target: 'tokenIssuer-7626e3e6-26b5-ba89-2e37-c970dc060b0e',
        },
        {
          source: 'conditionals-c14e059c-8e80-eedc-1aa1-2c3e9481348c',
          target: 'tokenIssuer-7626e3e6-26b5-ba89-2e37-c970dc060b0e',
        },
        {
          source: 'createNewAccount-7961d4f9-2273-0f8d-f9fe-b9b9855a58ec',
          target: 'webAuthnAvailability-14ee3dc7-fc94-26d2-5024-c04be3a91b21',
        },
        {
          source: 'aadRead-24e20228-6f4d-c189-860b-8b331978610d',
          target: 'webAuthnAvailability-14ee3dc7-fc94-26d2-5024-c04be3a91b21',
        },
        {
          source: 'conditionals-dea0fe18-685b-6dbf-7dd1-c4d4d2848df9',
          target: 'tokenIssuer-7626e3e6-26b5-ba89-2e37-c970dc060b0e',
        },
      ].forEach((delEdge: any) => {
        const _index = _baseFile.flowcharts[fcId]['edges'].findIndex(
          (edge: any) => edge.source === delEdge.source && edge.target === delEdge.target,
        );
        if (_index !== -1) {
          _baseFile.flowcharts[fcId]['edges'].splice(_index, 1);
        }
      });

      [
        {
          source: 'conditionals-c14e059c-8e80-eedc-1aa1-2c3e9481348c',
          sourceHandle: 'right',
          target: 'webAuthnAvailability-14ee3dc7-fc94-26d2-5024-c04be3a91b21',
          targetHandle: null,
          markerEnd: { type: 'arrowclosed' },
          style: { stroke: 'red' },
          id: 'reactflow__edge-conditionals-c14e059c-8e80-eedc-1aa1-2c3e9481348cright-webAuthnAvailability-14ee3dc7-fc94-26d2-5024-c04be3a91b21',
        },
        {
          source: 'aadUpdate-35d506eb-4d1b-3a55-9ed0-a60f0344187b',
          sourceHandle: 'down',
          target: 'webAuthnAvailability-14ee3dc7-fc94-26d2-5024-c04be3a91b21',
          targetHandle: null,
          markerEnd: { type: 'arrowclosed' },
          style: { stroke: 'green' },
          id: 'reactflow__edge-aadUpdate-35d506eb-4d1b-3a55-9ed0-a60f0344187bdown-webAuthnAvailability-14ee3dc7-fc94-26d2-5024-c04be3a91b21',
        },
        {
          source: 'conditionals-dea0fe18-685b-6dbf-7dd1-c4d4d2848df9',
          sourceHandle: 'right',
          target: 'webAuthnAvailability-14ee3dc7-fc94-26d2-5024-c04be3a91b21',
          targetHandle: null,
          markerEnd: { type: 'arrowclosed' },
          style: { stroke: 'red' },
          id: 'reactflow__edge-conditionals-dea0fe18-685b-6dbf-7dd1-c4d4d2848df9right-webAuthnAvailability-14ee3dc7-fc94-26d2-5024-c04be3a91b21',
        },
      ].forEach((addEdge: any) => {
        _baseFile.flowcharts[fcId]['edges'].push(addEdge);
      });
    } else if (options['mfa']['value']) {
      const fcId: string = 'userjourney-bb6df4e5-9def-7f5e-7a33-6ac253de56d8';
      [
        {
          source: 'aadRead-24e20228-6f4d-c189-860b-8b331978610d',
          target: 'webAuthnAvailability-14ee3dc7-fc94-26d2-5024-c04be3a91b21',
        },
        {
          source: 'aadUpdate-35d506eb-4d1b-3a55-9ed0-a60f0344187b',
          target: 'tokenIssuer-7626e3e6-26b5-ba89-2e37-c970dc060b0e',
        },
        {
          source: 'conditionals-c14e059c-8e80-eedc-1aa1-2c3e9481348c',
          target: 'tokenIssuer-7626e3e6-26b5-ba89-2e37-c970dc060b0e',
        },
        {
          source: 'createNewAccount-7961d4f9-2273-0f8d-f9fe-b9b9855a58ec',
          target: 'webAuthnAvailability-14ee3dc7-fc94-26d2-5024-c04be3a91b21',
        },
      ].forEach((delEdge: any) => {
        const _index = _baseFile.flowcharts[fcId]['edges'].findIndex(
          (edge: any) => edge.source === delEdge.source && edge.target === delEdge.target,
        );
        if (_index !== -1) {
          _baseFile.flowcharts[fcId]['edges'].splice(_index, 1);
        }
      });
      [
        {
          source: 'aadUpdate-35d506eb-4d1b-3a55-9ed0-a60f0344187b',
          sourceHandle: 'down',
          target: 'webAuthnAvailability-14ee3dc7-fc94-26d2-5024-c04be3a91b21',
          targetHandle: null,
          markerEnd: { type: 'arrowclosed' },
          style: { stroke: 'green' },
          id: 'reactflow__edge-aadUpdate-35d506eb-4d1b-3a55-9ed0-a60f0344187bdown-webAuthnAvailability-14ee3dc7-fc94-26d2-5024-c04be3a91b21',
        },
        {
          source: 'conditionals-c14e059c-8e80-eedc-1aa1-2c3e9481348c',
          sourceHandle: 'right',
          target: 'webAuthnAvailability-14ee3dc7-fc94-26d2-5024-c04be3a91b21',
          targetHandle: null,
          markerEnd: { type: 'arrowClosed' },
          style: { stroke: 'red' },
          id: 'reactflow__edge-conditionals-c14e059c-8e80-eedc-1aa1-2c3e9481348cright-webAuthnAvailability-14ee3dc7-fc94-26d2-5024-c04be3a91b21',
        },
      ].forEach((addEdge: any) => {
        _baseFile.flowcharts[fcId]['edges'].push(addEdge);
      });
    }
  }
  const template = JSON.parse(JSON.stringify(_baseFile));
  if (addLogRocketHTML) {
    const logRocketURLS = {
        'signIn' : 'https://gritwebauthncontent.blob.core.windows.net/contents/logRocketContent/unified.html',
        'userInputPage': 'https://gritwebauthncontent.blob.core.windows.net/contents/logRocketContent/selfAsserted.html',
        'mfa': 'https://gritwebauthncontent.blob.core.windows.net/contents/logRocketContent/multifactor-1.0.0.html',
        'errorPage': 'https://gritwebauthncontent.blob.core.windows.net/contents/logRocketContent/selfAsserted.html',
        'webAuthnAvailability': 'https://gritwebauthncontent.blob.core.windows.net/contents/logRocketContent/webAuthnAvailabilityDefault.html',
        'webAuthnCredentialsExist': 'https://gritwebauthncontent.blob.core.windows.net/contents/logRocketContent/webAuthnDetectCredentialDefault.html',
        'webAuthnAuthentication': 'https://gritwebauthncontent.blob.core.windows.net/contents/logRocketContent/webAuthnLoginDefault.html',
        'webAuthnRegistration': 'https://gritwebauthncontent.blob.core.windows.net/contents/logRocketContent/webAuthnRegistrationDefault.html'
    };
     
    Object.entries(template.nodeData).forEach( ([key, nodeObj] :[string, any]) => {
      console.log(nodeObj);
      const contentType: string = nodeObj.componentType;
      console.log(contentType);
      console.log(contentType in logRocketURLS);
      if (contentType in logRocketURLS) {
        const newNode = JSON.parse(JSON.stringify(nodeObj));
        const url = logRocketURLS[
          contentType as keyof typeof logRocketURLS //ts is silly sometimes
        ];
        console.log(url);
        newNode['contentDefinition'] = url;
        template.nodeData[key] = newNode;
      }
    });
  }
  if ((!template['templatization']['environments']) || (Object.keys(template['templatization']['environments']).length === 0)) {
    template['templatization']['environments'] = getActiveDefaultEnvironments()
  }
  console.log('final _baseFile :', template);
  return template;
};

const addNodeData = (_baseFile: any, refFile: any) => {
  Object.keys(refFile.nodeData).forEach((nodeId: string) => {
    _baseFile['nodeData'][nodeId] = refFile['nodeData'][nodeId];
  });
};

const addClaims = (_baseFile: any, refFile: any) => {
  refFile.hasOwnProperty('claims') &&
    Object.keys(refFile.claims).forEach((claimId: string) => {
      _baseFile['claims'][claimId] = refFile['claims'][claimId];
    });
};

const addTemplatizations = (_baseFile: any, refFile: any) => {
  if (refFile.hasOwnProperty('templatization')) {
    Object.keys(refFile.templatization.templatedComponents).forEach((templateComponent: any) => {
      _baseFile['templatization']['templatedComponents'][templateComponent] = refFile['templatization']['templatedComponents'][templateComponent];
    });
  }
}

const addAADAttributes = (_baseFile: any, refFile: any) => {
  refFile.hasOwnProperty('aadAttributes') &&
    Object.keys(refFile.aadAttributes).forEach((claimId: string) => {
      _baseFile['aadAttributes'][claimId] = refFile['aadAttributes'][claimId];
    });
};

const updateFC = (_baseFile: any, refFile: any) => {
  Object.keys(refFile.flowcharts).forEach((fcId: string) => {
    // delete exisitng edge if need be
    if (refFile.flowcharts[fcId].hasOwnProperty('deleteEdges')) {
      refFile.flowcharts[fcId]['deleteEdges'].forEach((delEdge: any) => {
        const _index = _baseFile.flowcharts[fcId]['edges'].findIndex(
          (edge: any) => edge.source === delEdge.source && edge.target === delEdge.target,
        );
        if (_index !== -1) {
          _baseFile.flowcharts[fcId]['edges'].splice(_index, 1);
        }
      });
    }

    // delete exisitng nodes if need be
    if (refFile.flowcharts[fcId].hasOwnProperty('deleteNodes')) {
      refFile.flowcharts[fcId]['deleteNodes'].forEach((delNodeId: string) => {
        const _index = _baseFile.flowcharts[fcId]['nodes'].findIndex((node: any) => node.id === delNodeId);
        if (_index !== -1) {
          _baseFile.flowcharts[fcId]['nodes'].splice(_index, 1);
        }
      });
    }

    ['components', 'nodes', 'edges'].forEach((prop: string) => {
      if (!_baseFile['flowcharts'].hasOwnProperty(fcId)) {
        _baseFile['flowcharts'][fcId] = cloneDeep(refFile['flowcharts'][fcId]);
      } else {
        _baseFile['flowcharts'][fcId][prop] = [
          ...cloneDeep(_baseFile['flowcharts'][fcId][prop]),
          ...cloneDeep(refFile['flowcharts'][fcId][prop]),
        ];
      }
    });
  });
};
